<template>
  <DxToolbar class="menubar">
    <DxItem :options="backButtonOptions" location="before" widget="dxButton" />
  </DxToolbar>

  <h2 class="content-block">Bulk Operations : Site Creation</h2>

  <!-- Step 1 : file -->
  <div class="content-block dx-card responsive-paddings">
    <div class="step-head">
      <span class="step-no">1</span>
      <h5 class="step-title">Upload a file</h5>
    </div>
    <p class="muted step-help">
      Select an Excel (.xlsx / .xls) or CSV file. The first row must contain the
      column headers. The file is read in your browser &mdash; nothing is sent
      to the server until you press Submit.
    </p>

    <div class="upload-row">
      <DxFileUploader
        ref="fileUploader"
        class="uploader"
        :multiple="false"
        :allowed-file-extensions="allowedFileTypes"
        :max-file-size="maxFileSize"
        upload-mode="useForm"
        select-button-text="Upload File"
        label-text="or drop a file here"
        accept=".xlsx,.xls,.csv"
        @value-changed="onFileSelected"
      />
      <DxButton
        text="Download Template"
        icon="download"
        styling-mode="outlined"
        @click="downloadTemplate"
      />
    </div>

    <div v-if="selectedFile" class="file-info">
      <span class="file-name">{{ selectedFile.name }}</span>
      <span class="muted">{{ formatFileSize(selectedFile.size) }}</span>
      <span v-if="rows.length" class="chip">{{ rows.length }} rows parsed</span>
      <span v-if="unmappedHeaders.length" class="chip chip-warn">
        Ignored columns: {{ unmappedHeaders.join(", ") }}
      </span>
    </div>

    <div v-if="parseError" class="parse-error">{{ parseError }}</div>
  </div>

  <!-- Step 2 : review + submit -->
  <div v-if="rows.length" class="content-block dx-card responsive-paddings">
    <div class="step-head">
      <span class="step-no">2</span>
      <h5 class="step-title">Review &amp; submit</h5>
    </div>
    <p class="muted step-help">
      Check the parsed data below. Any row can be edited in place; rows that
      have been created successfully are locked and are skipped when you
      re-submit. Expand a row to see the full error detail and the payload that
      was sent.
    </p>

    <div class="summary-row">
      <span class="stat"><b>{{ counts.pending }}</b> pending</span>
      <span class="stat stat-ok"><b>{{ counts.success }}</b> created</span>
      <span class="stat stat-bad"><b>{{ counts.failed }}</b> failed</span>
      <DxProgressBar
        v-if="isProcessing || counts.processed > 0"
        class="progress"
        :min="0"
        :max="rows.length"
        :value="counts.processed"
        :show-status="false"
      />
    </div>

    <div class="action-buttons">
      <DxButton
        :text="submitButtonText"
        icon="check"
        type="success"
        styling-mode="contained"
        :disabled="!canSubmit"
        @click="onSubmitAll"
      />
      <DxButton
        v-if="counts.failed > 0 && !isProcessing"
        text="Retry Failed"
        icon="refresh"
        type="default"
        styling-mode="outlined"
        @click="onRetryFailed"
      />
      <DxButton
        v-if="isProcessing"
        text="Stop"
        icon="clear"
        type="danger"
        styling-mode="outlined"
        @click="cancelRequested = true"
      />
      <DxButton
        text="Clear"
        icon="trash"
        styling-mode="outlined"
        :disabled="isProcessing"
        @click="onClear"
      />
    </div>

    <DxDataGrid
      ref="grid"
      class="results-grid"
      :data-source="rows"
      key-expr="rowId"
      :show-borders="true"
      :show-row-lines="true"
      :column-auto-width="true"
      :allow-column-resizing="true"
      :word-wrap-enabled="true"
      :repaint-changes-only="true"
      @initialized="saveGridInstance"
      @row-prepared="onRowPrepared"
      @row-updated="onRowUpdated"
      @editing-start="onEditingStart"
    >
      <DxEditing
        mode="row"
        :allow-updating="allowUpdating"
        :allow-deleting="allowDeleting"
        :use-icons="true"
      />
      <DxPaging :enabled="false" />
      <DxScrolling mode="standard" row-rendering-mode="virtual" />
      <DxExport :enabled="true" :formats="['xlsx']" />
      <DxMasterDetail :enabled="true" template="detail" />

      <DxColumn
        data-field="status"
        caption="Progress"
        :width="130"
        :allow-editing="false"
        alignment="center"
        cell-template="progress-cell"
      />
      <DxColumn
        data-field="rowNumber"
        caption="Row"
        :width="60"
        :allow-editing="false"
        alignment="center"
      />
      <DxColumn data-field="containerName" caption="Site Name" :width="170" />
      <DxColumn
        data-field="containerTypeName"
        caption="Container Type"
        :width="150"
      >
        <DxLookup
          :data-source="lookups.containerTypes"
          value-expr="containerTypeName"
          display-expr="containerTypeName"
        />
      </DxColumn>
      <DxColumn data-field="operatorName" caption="Operator" :width="150">
        <DxLookup
          :data-source="lookups.operators"
          value-expr="operatorName"
          display-expr="operatorName"
        />
      </DxColumn>
      <DxColumn data-field="areaName" caption="Area" :width="150">
        <DxLookup
          :data-source="lookups.areas"
          value-expr="areaName"
          display-expr="areaName"
        />
      </DxColumn>
      <DxColumn data-field="aliasValue" caption="Alias" :width="140" />
      <DxColumn data-field="downholeValue" caption="Downhole LSD" :width="170" />
      <DxColumn data-field="deviceSerial" caption="Serial Number" :width="140" />
      <DxColumn data-field="deviceTypeName" caption="Device Type" :width="140">
        <DxLookup
          :data-source="lookups.deviceTypes"
          value-expr="deviceTypeName"
          display-expr="deviceTypeName"
        />
      </DxColumn>
      <DxColumn data-field="contractName" caption="Contract" :width="140">
        <DxLookup
          :data-source="lookups.contracts"
          value-expr="contractName"
          display-expr="contractName"
        />
      </DxColumn>
      <DxColumn
        data-field="containerId"
        caption="Site ID"
        :width="90"
        :allow-editing="false"
        alignment="center"
      />
      <DxColumn
        data-field="message"
        caption="Result / Error"
        :min-width="220"
        :allow-editing="false"
        cell-template="message-cell"
      />
      <DxColumn type="buttons" caption="" :width="90">
        <DxGridButton name="edit" />
        <DxGridButton
          hint="Submit this row"
          icon="upload"
          :visible="isRowSubmittable"
          :on-click="onSubmitSingleRow"
        />
      </DxColumn>

      <template #progress-cell="{ data }">
        <span class="status-badge" :class="statusClass(data.data.status)">
          <i
            v-if="data.data.status === 'processing'"
            class="dx-icon dx-icon-clock spin"
          ></i>
          {{ statusLabel(data.data.status) }}
        </span>
      </template>

      <template #message-cell="{ data }">
        <span :class="{ 'error-text': isFailed(data.data.status) }">{{
          data.value
        }}</span>
      </template>

      <template #detail="{ data }">
        <div class="row-detail">
          <div class="detail-block">
            <h6>Status</h6>
            <p>
              {{ statusLabel(data.data.status) }}
              <span v-if="data.data.attempts" class="muted">
                &middot; {{ data.data.attempts }} attempt(s)</span
              >
            </p>
          </div>
          <div class="detail-block">
            <h6>Message</h6>
            <p :class="{ 'error-text': isFailed(data.data.status) }">
              {{ data.data.message || "&mdash;" }}
            </p>
          </div>
          <div
            v-if="data.data.errors && data.data.errors.length"
            class="detail-block"
          >
            <h6>Validation</h6>
            <ul class="error-list">
              <li v-for="(e, i) in data.data.errors" :key="i" class="error-text">
                {{ e }}
              </li>
            </ul>
          </div>
          <div v-if="data.data.lastPayload" class="detail-block">
            <h6>Payload sent</h6>
            <pre class="payload">{{ prettyPayload(data.data.lastPayload) }}</pre>
          </div>
        </div>
      </template>
    </DxDataGrid>
  </div>

  <DxLoadPanel
    :visible="isLoadingLookups"
    :show-indicator="true"
    :show-pane="true"
    :shading="true"
    shading-color="rgba(0,0,0,0.4)"
    message="Loading reference data..."
  />
</template>

<script>
import DxToolbar, { DxItem } from "devextreme-vue/toolbar";
import { DxButton } from "devextreme-vue/button";
import DxFileUploader from "devextreme-vue/file-uploader";
import DxProgressBar from "devextreme-vue/progress-bar";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxExport,
  DxLookup,
  DxMasterDetail,
  DxPaging,
  DxScrolling,
  DxButton as DxGridButton,
} from "devextreme-vue/data-grid";
import { confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import * as XLSX from "xlsx";

// Canonical spreadsheet columns. `aliases` are matched against the file's
// header row after stripping everything but letters and digits, so
// "Container Type", "container_type" and "containertype" all resolve.
const FIELDS = [
  {
    key: "containerName",
    caption: "Site Name",
    required: true,
    aliases: ["sitename", "name", "containername", "site", "tankname"],
  },
  {
    key: "containerTypeName",
    caption: "Container Type",
    required: true,
    lookup: "containerTypes",
    idKey: "containerTypeId",
    nameProp: "containerTypeName",
    aliases: ["containertype", "type", "tanktype"],
  },
  {
    key: "operatorName",
    caption: "Operator",
    required: true,
    lookup: "operators",
    idKey: "operatorId",
    nameProp: "operatorName",
    aliases: ["operator", "operatorname"],
  },
  {
    key: "areaName",
    caption: "Area",
    required: true,
    lookup: "areas",
    idKey: "areaId",
    nameProp: "areaName",
    aliases: ["area", "areaname"],
  },
  {
    key: "aliasValue",
    caption: "Alias",
    propertyName: "Alias",
    aliases: ["alias", "sitealias", "containeralias"],
  },
  {
    key: "downholeValue",
    caption: "Downhole LSD",
    propertyName: "Downhole LSD",
    aliases: ["downhole", "downholelsd", "lsd"],
  },
  {
    key: "deviceSerial",
    caption: "Serial Number",
    aliases: [
      "serialnumber",
      "serial",
      "deviceserial",
      "serialnum",
      "devicename",
    ],
  },
  {
    key: "deviceTypeName",
    caption: "Device Type",
    lookup: "deviceTypes",
    idKey: "deviceTypeId",
    nameProp: "deviceTypeName",
    aliases: ["devicetype"],
  },
  {
    key: "contractName",
    caption: "Contract",
    lookup: "contracts",
    idKey: "contractId",
    nameProp: "contractName",
    aliases: ["contract", "contracttype", "contractname"],
  },
];

// Columns written straight onto the site record, vs. those that become
// entries in the payload's `properties` array.
const SITE_FIELDS = FIELDS.filter((f) => !f.propertyName);
const PROPERTY_FIELDS = FIELDS.filter((f) => f.propertyName);

const normalise = (v) =>
  String(v == null ? "" : v)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export default {
  name: "BulkSiteCreate",
  components: {
    DxToolbar,
    DxItem,
    DxButton,
    DxFileUploader,
    DxProgressBar,
    DxLoadPanel,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxExport,
    DxLookup,
    DxMasterDetail,
    DxPaging,
    DxScrolling,
    DxGridButton,
  },
  data() {
    return {
      maxFileSize: 10 * 1024 * 1024,
      allowedFileTypes: [".xlsx", ".xls", ".csv"],
      selectedFile: null,
      parseError: "",
      unmappedHeaders: [],
      rows: [],
      isProcessing: false,
      isLoadingLookups: false,
      cancelRequested: false,
      gridInstance: null,
      lookups: {
        containerTypes: [],
        operators: [],
        areas: [],
        deviceTypes: [],
        contracts: [],
        properties: [],
      },
      backButtonOptions: {
        icon: "revert",
        onClick: () => this.$router.push("/bulk-operations"),
      },
    };
  },
  created() {
    this.loadLookups();
  },
  computed: {
    counts() {
      const c = { pending: 0, success: 0, failed: 0, processed: 0 };
      for (const r of this.rows) {
        if (r.status === "success") c.success++;
        else if (r.status === "error" || r.status === "invalid") c.failed++;
        else c.pending++;
      }
      c.processed = c.success + c.failed;
      return c;
    },
    canSubmit() {
      return !this.isProcessing && this.counts.pending + this.counts.failed > 0;
    },
    submitButtonText() {
      if (this.isProcessing) return "Submitting...";
      const remaining = this.counts.pending + this.counts.failed;
      return this.counts.success > 0
        ? `Submit Remaining (${remaining})`
        : `Submit (${remaining})`;
    },
  },
  methods: {
    /* ---------- reference data ---------- */

    async loadLookups() {
      this.isLoadingLookups = true;
      try {
        await Promise.all([
          this.$store.dispatch("containerTypes/loadContainerTypes"),
          this.$store.dispatch("operators/loadOperators"),
          this.$store.dispatch("areas/loadAreas"),
          this.$store.dispatch("deviceTypes/loadDeviceTypes"),
          this.$store.dispatch("contracts/loadContracts"),
          this.$store.dispatch("properties/loadProperties"),
        ]);
        this.lookups.containerTypes =
          this.$store.getters["containerTypes/getContainerTypes"] || [];
        this.lookups.operators =
          this.$store.getters["operators/getOperators"] || [];
        this.lookups.areas = this.$store.getters["areas/getAreas"] || [];
        this.lookups.deviceTypes =
          this.$store.getters["deviceTypes/getDeviceTypes"] || [];
        this.lookups.contracts =
          this.$store.getters["contracts/getContracts"] || [];
        this.lookups.properties =
          this.$store.getters["properties/getProperties"] || [];
      } catch (e) {
        console.error("Failed to load reference data", e);
        notify(
          "Could not load reference data. Row validation will fail until it loads.",
          "error",
          4000
        );
      } finally {
        this.isLoadingLookups = false;
      }
    },

    /* ---------- file handling ---------- */

    downloadTemplate() {
      if (this.isLoadingLookups) {
        notify("Reference data is still loading. Try again in a moment.", "warning", 2500);
        return;
      }

      const wb = XLSX.utils.book_new();

      // Sheet 1 holds the headers only. Everything below row 1 is imported
      // verbatim, so shipping a sample row would create a junk site for anyone
      // who pasted their data underneath it instead of over it.
      const sites = XLSX.utils.aoa_to_sheet([FIELDS.map((f) => f.caption)]);
      sites["!cols"] = FIELDS.map((f) => ({
        wch: Math.max(16, f.caption.length + 4),
      }));
      XLSX.utils.book_append_sheet(wb, sites, "Sites");

      // Sheet 2 is reference material. Only the first sheet is read back on
      // upload, so this cannot be mistaken for data.
      const reference = XLSX.utils.aoa_to_sheet(this.buildReferenceRows());
      reference["!cols"] = [{ wch: 26 }, { wch: 26 }, { wch: 26 }, { wch: 26 }, { wch: 26 }];
      XLSX.utils.book_append_sheet(wb, reference, "Reference Values");

      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .substring(0, 19);
      XLSX.writeFile(wb, `bulk_site_create_template_${stamp}.xlsx`);
      notify("Template downloaded", "success", 2000);
    },

    // Builds the Reference Values sheet: the accepted value for every lookup
    // column, and which container types each property column applies to. Both
    // are derived from the same data the validator uses, so the template can
    // never advertise a value the screen would then reject.
    buildReferenceRows() {
      const columns = [
        {
          caption: "Container Type",
          values: this.lookups.containerTypes.map((x) => x.containerTypeName),
        },
        {
          caption: "Operator",
          values: this.lookups.operators.map((x) => x.operatorName),
        },
        { caption: "Area", values: this.lookups.areas.map((x) => x.areaName) },
        {
          caption: "Device Type",
          values: this.lookups.deviceTypes.map((x) => x.deviceTypeName),
        },
        {
          caption: "Contract",
          values: this.lookups.contracts.map((x) => x.contractName),
        },
      ];

      const rows = [
        ["Values accepted by each lookup column on the Sites sheet."],
        ["Matching ignores case, spacing and punctuation."],
        [],
        columns.map((c) => c.caption),
      ];

      const depth = columns.reduce((m, c) => Math.max(m, c.values.length), 0);
      for (let i = 0; i < depth; i++) {
        rows.push(columns.map((c) => c.values[i] || ""));
      }

      rows.push([]);
      rows.push(["Alias and Downhole LSD are container properties, not site fields."]);
      rows.push(["They are only accepted for the container types listed below."]);
      rows.push([]);
      rows.push(["Property", "Accepted for container types"]);

      PROPERTY_FIELDS.forEach((field) => {
        const types = this.lookups.containerTypes
          .filter((ct) => this.findProperty(field.propertyName, ct.containerTypeId))
          .map((ct) => ct.containerTypeName);
        rows.push([
          field.caption,
          types.length ? types.join(", ") : "not configured — leave blank",
        ]);
      });

      rows.push([]);
      rows.push(["Serial Number is optional, but supplying one also requires"]);
      rows.push(["Device Type and Contract on the same row."]);

      return rows;
    },

    onFileSelected(e) {
      const files = e.value;
      this.parseError = "";
      this.unmappedHeaders = [];
      this.rows = [];

      if (!files || !files.length) {
        this.selectedFile = null;
        return;
      }
      this.selectedFile = files[0];
      this.parseFile(this.selectedFile);
    },

    parseFile(file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const wb = XLSX.read(new Uint8Array(evt.target.result), {
            type: "array",
          });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          if (!sheet) throw new Error("the file contains no worksheets.");

          const matrix = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
            blankrows: false,
            raw: false,
          });
          if (!matrix.length) throw new Error("the file is empty.");

          this.buildRows(matrix);
        } catch (err) {
          console.error("Parse error", err);
          this.rows = [];
          this.parseError = `Could not read the file: ${err.message}`;
          notify(this.parseError, "error", 4000);
        }
      };
      reader.onerror = () => {
        this.parseError = "Could not read the file from disk.";
        notify(this.parseError, "error", 4000);
      };
      reader.readAsArrayBuffer(file);
    },

    // Maps the header row onto FIELDS and turns the remaining rows into grid
    // records. Unrecognised headers are reported but not imported.
    buildRows(matrix) {
      const headerRow = matrix[0].map((h) => normalise(h));
      const columnFor = {};
      const used = new Set();

      FIELDS.forEach((field) => {
        const idx = headerRow.findIndex(
          (h, i) =>
            !used.has(i) &&
            h &&
            (h === normalise(field.caption) || field.aliases.includes(h))
        );
        if (idx > -1) {
          columnFor[field.key] = idx;
          used.add(idx);
        }
      });

      const missingRequired = FIELDS.filter(
        (f) => f.required && columnFor[f.key] === undefined
      ).map((f) => f.caption);

      if (missingRequired.length) {
        this.rows = [];
        this.parseError =
          `Required column(s) not found in the file: ${missingRequired.join(", ")}. ` +
          "Download the template for the expected headers.";
        notify(this.parseError, "error", 5000);
        return;
      }

      this.unmappedHeaders = matrix[0].filter(
        (h, i) => !used.has(i) && String(h).trim().length > 0
      );

      const rows = [];
      for (let r = 1; r < matrix.length; r++) {
        const raw = matrix[r];
        const rec = {
          rowId: r,
          rowNumber: r + 1, // spreadsheet row number; the header is row 1
          status: "pending",
          message: "",
          errors: [],
          containerId: null,
          attempts: 0,
          lastPayload: null,
        };
        FIELDS.forEach((f) => {
          const idx = columnFor[f.key];
          const value = idx === undefined ? "" : raw[idx];
          rec[f.key] = value == null ? "" : String(value).trim();
        });

        // skip rows that are entirely blank
        if (FIELDS.some((f) => rec[f.key])) rows.push(rec);
      }

      if (!rows.length) {
        this.parseError = "No data rows were found below the header row.";
        notify(this.parseError, "warning", 4000);
        return;
      }

      this.rows = rows;
      notify(
        `${rows.length} row(s) parsed from ${this.selectedFile.name}`,
        "success",
        2500
      );
    },

    /* ---------- validation ---------- */

    findLookup(field, value) {
      const list = this.lookups[field.lookup] || [];
      const target = normalise(value);
      return list.find((item) => normalise(item[field.nameProp]) === target);
    },

    // Properties are defined per container type, so the same property name can
    // carry a different propertyId for each. Prefer the definition that matches
    // the row's container type, then fall back to a type-agnostic one.
    findProperty(propertyName, containerTypeId) {
      const target = normalise(propertyName);
      const candidates = (this.lookups.properties || []).filter(
        (p) =>
          normalise(p.propertyName) === target &&
          (!p.appliesTo || p.appliesTo === "C")
      );
      return (
        candidates.find((p) => p.containerTypeId === containerTypeId) ||
        candidates.find((p) => p.containerTypeId == null)
      );
    },

    // Turns a grid row into the payload expected by utils/addSite, or returns
    // the list of reasons it cannot be built.
    resolveRow(row) {
      const errors = [];
      const ids = {};

      SITE_FIELDS.forEach((field) => {
        const value = (row[field.key] || "").trim();

        if (!value) {
          if (field.required) errors.push(`${field.caption} is required.`);
          return;
        }
        if (!field.lookup) return;

        const match = this.findLookup(field, value);
        if (!match) {
          errors.push(
            `${field.caption} "${value}" does not match an existing record.`
          );
          return;
        }
        ids[field.idKey] = match[field.idKey];
      });

      // Alias and Downhole LSD are container properties rather than site
      // columns, so they travel in the payload's `properties` array.
      const properties = [];
      PROPERTY_FIELDS.forEach((field) => {
        const value = (row[field.key] || "").trim();
        if (!value) return;

        // container type could not be resolved; that failure is already reported
        if (!ids.containerTypeId) return;

        const property = this.findProperty(field.propertyName, ids.containerTypeId);
        if (!property) {
          errors.push(
            `${field.caption} is not a configured property for container type "${row.containerTypeName}".`
          );
          return;
        }
        properties.push({
          propertyId: property.propertyId,
          propValue: value,
          marginal: null,
          critical: null,
        });
      });

      const serial = (row.deviceSerial || "").trim();
      if (serial) {
        if (!(row.deviceTypeName || "").trim())
          errors.push("Device Type is required when a Serial Number is supplied.");
        if (!(row.contractName || "").trim())
          errors.push("Contract is required when a Serial Number is supplied.");
      }

      if (errors.length) return { ok: false, errors };

      const devices = serial
        ? [
            {
              deviceName: serial,
              deviceTypeId: ids.deviceTypeId,
              contractId: ids.contractId,
            },
          ]
        : [];

      return {
        ok: true,
        errors: [],
        payload: {
          containerName: row.containerName.trim(),
          containerTypeId: ids.containerTypeId,
          deviceSerial: serial,
          operatorId: ids.operatorId,
          areaId: ids.areaId,
          properties,
          devices,
        },
      };
    },

    /* ---------- submission ---------- */

    async onSubmitAll() {
      const targets = this.rows.filter((r) => r.status !== "success");
      if (!targets.length) return;

      const ok = await confirm(
        `<p>Create <b>${targets.length}</b> site(s) from
         <b>${this.selectedFile ? this.selectedFile.name : "the uploaded file"}</b>?</p>
         <p>Each row is submitted individually. Rows already created are skipped.</p>`,
        "Confirm Bulk Site Creation"
      );
      if (!ok) return;

      await this.runBatch(targets);
    },

    async onRetryFailed() {
      const targets = this.rows.filter((r) => this.isFailed(r.status));
      if (!targets.length) return;
      await this.runBatch(targets);
    },

    async onSubmitSingleRow(e) {
      const row = e.row.data;
      if (row.status === "success" || this.isProcessing) return;

      this.isProcessing = true;
      this.cancelRequested = false;
      try {
        const created = await this.submitRow(row);
        notify(
          created
            ? `Row ${row.rowNumber} created.`
            : `Row ${row.rowNumber} failed. ${row.message}`,
          created ? "success" : "error",
          4000
        );
      } finally {
        this.isProcessing = false;
      }
    },

    async runBatch(targets) {
      this.isProcessing = true;
      this.cancelRequested = false;

      // reset previous failures back to pending so the progress bar reads cleanly
      targets.forEach((r) =>
        this.updateRow(r, { status: "pending", message: "", errors: [] })
      );

      let created = 0;
      let failed = 0;
      try {
        for (const row of targets) {
          if (this.cancelRequested) break;
          if (await this.submitRow(row)) created++;
          else failed++;
        }
      } finally {
        this.isProcessing = false;
      }

      if (this.cancelRequested) {
        notify(`Stopped. ${created} created, ${failed} failed.`, "warning", 4000);
      } else if (failed === 0) {
        notify(`All done — ${created} site(s) created.`, "success", 4000);
      } else {
        notify(
          `${created} site(s) created, ${failed} failed. Fix the highlighted rows and retry.`,
          "error",
          5000
        );
      }
    },

    // Submits one row and records the outcome on the row itself.
    async submitRow(row) {
      if (row.status === "success") return true;

      this.updateRow(row, { status: "processing", message: "", errors: [] });

      const resolved = this.resolveRow(row);
      if (!resolved.ok) {
        this.updateRow(row, {
          status: "invalid",
          message: resolved.errors.join(" "),
          errors: resolved.errors,
        });
        return false;
      }

      this.updateRow(row, {
        attempts: row.attempts + 1,
        lastPayload: resolved.payload,
      });

      try {
        await this.$store.dispatch("utils/addSite", resolved.payload);
        const result = this.$store.getters["utils/getAddSiteResults"];

        if (result && result.recordsAffected) {
          this.updateRow(row, {
            status: "success",
            containerId: result.recordsAffected,
            message: result.message || "Created.",
          });
          return true;
        }

        this.updateRow(row, {
          status: "error",
          message:
            result && result.message
              ? String(result.message).split(";").join(" ").trim()
              : "The site was not created.",
        });
        return false;
      } catch (err) {
        console.error("addSite failed", err);
        this.updateRow(row, {
          status: "error",
          message: err.message || "The request failed.",
        });
        return false;
      }
    },

    // Writes to the row and repaints just that row, so the grid keeps its
    // scroll position and any expanded detail panels.
    updateRow(row, patch) {
      Object.assign(row, patch);
      if (this.gridInstance) {
        const index = this.gridInstance.getRowIndexByKey(row.rowId);
        if (index >= 0) this.gridInstance.repaintRows([index]);
      }
    },

    /* ---------- grid plumbing ---------- */

    saveGridInstance(e) {
      this.gridInstance = e.component;
    },

    allowUpdating(e) {
      return e.row.data.status !== "success" && !this.isProcessing;
    },

    allowDeleting(e) {
      return e.row.data.status !== "success" && !this.isProcessing;
    },

    onEditingStart(e) {
      // belt and braces — created rows are locked out entirely
      if (e.data.status === "success") {
        e.cancel = true;
        notify(
          "This site has already been created and can no longer be edited.",
          "info",
          2500
        );
      }
    },

    onRowUpdated(e) {
      // an edited row goes back into the queue
      if (e.data.status !== "success") {
        this.updateRow(e.data, { status: "pending", message: "", errors: [] });
      }
    },

    onRowPrepared(e) {
      if (e.rowType !== "data") return;
      switch (e.data.status) {
        case "success":
          e.rowElement.style.backgroundColor = "rgba(40, 167, 69, 0.18)";
          break;
        case "error":
        case "invalid":
          e.rowElement.style.backgroundColor = "rgba(220, 53, 69, 0.18)";
          break;
        case "processing":
          e.rowElement.style.backgroundColor = "rgba(255, 125, 0, 0.18)";
          break;
        default:
          e.rowElement.style.backgroundColor = "";
      }
    },

    isRowSubmittable(e) {
      return e.row.data.status !== "success";
    },

    /* ---------- misc ---------- */

    isFailed(status) {
      return status === "error" || status === "invalid";
    },

    statusLabel(status) {
      const labels = {
        pending: "Pending",
        processing: "Submitting",
        success: "Created",
        error: "Error",
        invalid: "Invalid",
      };
      return labels[status] || status;
    },

    statusClass(status) {
      return `status-${status}`;
    },

    prettyPayload(payload) {
      return JSON.stringify(payload, null, 2);
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
    },

    async onClear() {
      if (this.counts.success > 0) {
        const ok = await confirm(
          `<p>${this.counts.success} site(s) were created from this file.</p>
           <p>Clearing removes the results from this screen. The sites themselves are not affected.</p>`,
          "Clear results?"
        );
        if (!ok) return;
      }
      this.selectedFile = null;
      this.rows = [];
      this.parseError = "";
      this.unmappedHeaders = [];
      if (this.$refs.fileUploader) this.$refs.fileUploader.instance.reset();
    },
  },
};
</script>

<style scoped>
.muted {
  opacity: 0.7;
}

.step-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.step-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgb(255, 125, 0);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.step-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.step-help {
  margin: 0 0 16px 0;
  font-size: 13px;
  max-width: 760px;
}

.upload-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}

.uploader {
  flex: 1 1 320px;
  max-width: 520px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 12px;
  font-size: 13px;
}

.file-name {
  font-weight: 600;
}

.parse-error {
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 4px;
  border-left: 4px solid #dc3545;
  background: rgba(220, 53, 69, 0.12);
  color: #ff8a94;
  font-size: 13px;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  font-size: 13px;
}

.stat b {
  font-size: 15px;
}

.stat-ok b {
  color: #4caf50;
}

.stat-bad b {
  color: #ff5c69;
}

.progress {
  flex: 1 1 200px;
  max-width: 320px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.action-buttons .dx-button {
  min-width: 140px;
}

.results-grid {
  max-height: 60vh;
}

.chip {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 12px;
  background: rgba(255, 115, 0, 0.15);
  color: rgb(255, 140, 40);
}

.chip-warn {
  background: rgba(255, 193, 7, 0.15);
  color: #ffc107;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-pending {
  background: rgba(150, 150, 150, 0.25);
  color: #cfcfcf;
}

.status-processing {
  background: #ff7d00;
  color: #fff;
}

.status-success {
  background: #28a745;
  color: #fff;
}

.status-error,
.status-invalid {
  background: #dc3545;
  color: #fff;
}

.spin {
  animation: spin 1.2s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-text {
  color: #ff8a94;
}

.row-detail {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 14px 18px;
}

.detail-block h6 {
  margin: 0 0 6px 0;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
}

.detail-block p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

.error-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.6;
}

.payload {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  max-height: 220px;
  overflow: auto;
  padding: 8px 10px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.25);
}
</style>
